/**
 * Sub-Millisecond Fuzzy Trie & In-Memory Indexer
 * Provides prefix matching and fuzzy string distance traversal (Levenshtein <= 2)
 * for bookmarks, terminal commands, tags, and URLs.
 */

class TrieNode {
  constructor() {
    this.children = new Map();
    this.isEndOfWord = false;
    this.items = []; // Associated items/objects
    this.weight = 0; // Frequency or priority score
  }
}

export class FuzzyTrie {
  constructor() {
    this.root = new TrieNode();
    this.size = 0;
  }

  /**
   * Insert a string key into the Trie with an associated item payload
   * @param {string} key 
   * @param {any} item 
   * @param {number} weight 
   */
  insert(key, item, weight = 1) {
    if (!key || typeof key !== 'string') return;
    const normalized = key.trim().toLowerCase();
    if (!normalized) return;

    let current = this.root;
    for (let i = 0; i < normalized.length; i++) {
      const char = normalized[i];
      if (!current.children.has(char)) {
        current.children.set(char, new TrieNode());
      }
      current = current.children.get(char);
      current.weight = Math.max(current.weight, weight);
    }

    current.isEndOfWord = true;
    current.items.push({ item, key: normalized, weight });
    this.size++;
  }

  /**
   * Fast exact prefix search
   * @param {string} prefix 
   * @param {number} limit 
   * @returns {Array<{item: any, key: string, weight: number}>}
   */
  searchPrefix(prefix, limit = 10) {
    if (!prefix || typeof prefix !== 'string') return [];
    const normalized = prefix.trim().toLowerCase();
    if (!normalized) return [];

    let current = this.root;
    for (let i = 0; i < normalized.length; i++) {
      const char = normalized[i];
      if (!current.children.has(char)) {
        return [];
      }
      current = current.children.get(char);
    }

    const results = [];
    const dfs = (node) => {
      if (results.length >= limit * 2) return;
      if (node.isEndOfWord) {
        results.push(...node.items);
      }
      for (const child of node.children.values()) {
        dfs(child);
      }
    };

    dfs(current);
    return results
      .sort((a, b) => b.weight - a.weight)
      .slice(0, limit);
  }

  /**
   * Fuzzy traversal supporting character insertions, deletions, substitutions
   * @param {string} word 
   * @param {number} maxCost 
   * @param {number} limit 
   */
  searchFuzzy(word, maxCost = 2, limit = 8) {
    if (!word || typeof word !== 'string') return [];
    const target = word.trim().toLowerCase();
    if (!target) return [];

    // Current row initialized with 0..len
    const currentRow = Array.from({ length: target.length + 1 }, (_, i) => i);
    const results = [];

    const searchRecursive = (node, letter, previousRow) => {
      const columns = target.length + 1;
      const nextRow = [previousRow[0] + 1];

      for (let col = 1; col < columns; col++) {
        const insertCost = nextRow[col - 1] + 1;
        const deleteCost = previousRow[col] + 1;
        const replaceCost = (target[col - 1] === letter)
          ? previousRow[col - 1]
          : previousRow[col - 1] + 1;

        nextRow.push(Math.min(insertCost, deleteCost, replaceCost));
      }

      // If the last entry in the row is <= maxCost and this node is end of word
      if (nextRow[columns - 1] <= maxCost && node.isEndOfWord) {
        for (const it of node.items) {
          results.push({
            ...it,
            distance: nextRow[columns - 1],
            score: (10 / (nextRow[columns - 1] + 1)) + (it.weight * 0.1),
          });
        }
      }

      // If any entry in nextRow is <= maxCost, continue searching children
      if (Math.min(...nextRow) <= maxCost) {
        for (const [char, childNode] of node.children.entries()) {
          searchRecursive(childNode, char, nextRow);
        }
      }
    };

    for (const [char, childNode] of this.root.children.entries()) {
      searchRecursive(childNode, char, currentRow);
    }

    // Sort primarily by distance ascending, then by weight descending
    return results
      .sort((a, b) => (a.distance - b.distance) || (b.weight - a.weight))
      .slice(0, limit);
  }

  /**
   * Combined query: checks prefix first, falls back to fuzzy search
   * @param {string} query 
   * @param {number} limit 
   */
  query(query, limit = 8) {
    if (!query) return [];
    const prefixMatches = this.searchPrefix(query, limit);
    if (prefixMatches.length >= limit) {
      return prefixMatches;
    }

    const seen = new Set(prefixMatches.map(m => m.key));
    const fuzzyMatches = this.searchFuzzy(query, 2, limit);

    const merged = [...prefixMatches];
    for (const f of fuzzyMatches) {
      if (!seen.has(f.key) && merged.length < limit) {
        merged.push(f);
        seen.add(f.key);
      }
    }
    return merged;
  }
}

/**
 * Helper to build an index from bookmarks and command lists
 * @param {Array} bookmarks 
 * @param {Array} commands 
 * @returns {FuzzyTrie}
 */
export function buildSearchIndex(bookmarks = [], commands = []) {
  const trie = new FuzzyTrie();

  // Index bookmarks: title, URL, tags
  bookmarks.forEach((bm) => {
    const weight = (bm.clicks || 0) + (bm.isReadingList ? 5 : 0) + 1;
    if (bm.title) {
      trie.insert(bm.title, { ...bm, type: 'bookmark' }, weight);
      // Also index individual words in title
      bm.title.split(/\s+/).forEach((w) => {
        if (w.length > 2) trie.insert(w, { ...bm, type: 'bookmark' }, weight * 0.8);
      });
    }
    if (bm.url) {
      try {
        const host = new URL(bm.url).hostname.replace(/^www\./, '');
        trie.insert(host, { ...bm, type: 'bookmark' }, weight);
      } catch {
        // ignore
      }
    }
    if (bm.note) {
      const tags = bm.note.match(/#\w+/g) || [];
      tags.forEach(t => trie.insert(t, { ...bm, type: 'bookmark' }, weight + 2));
    }
  });

  // Index terminal / palette commands
  commands.forEach((cmd) => {
    trie.insert(cmd.name, { ...cmd, type: 'command' }, 10);
    if (cmd.aliases) {
      cmd.aliases.forEach(a => trie.insert(a, { ...cmd, type: 'command' }, 8));
    }
  });

  return trie;
}
